/**
 * Marketing-display source of truth for hosting plans.
 *
 * Three sources of truth coexist (M3.2 architecture, see
 * memory/marketing_v2_architecture_decisions.md / Architect's annotations
 * in MARKETING_IMPLEMENTATION_PLAN_2026-05-07.md §9):
 *
 *   1. service_plan table in Supabase  → CANONICAL for billing (runtime)
 *   2. THIS FILE                        → marketing-display (build-time)
 *   3. (legacy) inline arrays in pages  → REMOVED in M3.2
 *
 * The marketing/scripts/check-plan-drift.mjs prebuild step reads the active
 * service_plan rows from Supabase and refuses to build if priceZar here
 * disagrees with recurring_amount in DB. So changing prices requires
 * updating BOTH places, with the build catching mistakes.
 *
 * Feature lists are marketing copy and live ONLY here — they don't have a
 * DB equivalent. Slugs MUST match service_plan.slug exactly so the
 * marketing CTA → portal handshake (?plan=basic-monthly) resolves the
 * correct plan in the order funnel.
 */

import { siteConfig } from './site';

export type Unlimited = 'unlimited';

export interface HostingPlan {
  /** Human-readable name. Display only. */
  name: string;
  /** Slug — MUST match service_plan.slug. Used by the order-funnel handshake. */
  slug: string;
  /** Monthly price in ZAR (number). MUST match service_plan.recurring_amount. */
  priceZar: number;
  /** Optional one-line tagline shown on the featured plan card. */
  tagline?: string;
  /** Marketing feature bullets. Free-form copy; no DB equivalent. */
  features: string[];
  /** Featured / "Most Popular" — only ONE plan should have this true. */
  popular?: boolean;
}

// 2026-10-15: every plan said '24/7 Technical Support' while the desk is staffed
// Mon-Fri 08:30-16:30 (siteConfig.supportHours, confirmed by Mr Mampe
// 2026-08-11). The string also fed the Product structured data on /hosting.
// Platform monitoring IS round the clock, but that is not technical support.
// Reads the hours from siteConfig so there is one copy of them.
const { opens, closes } = siteConfig.supportHoursSpec;
const SUPPORT_FEATURE = `Local support, Mon-Fri ${opens}-${closes}`;

export const hostingPlans: HostingPlan[] = [
  {
    name: 'Entry',
    slug: 'entry-monthly',
    priceZar: 79,
    features: [
      'cPanel Control Panel Access',
      'FREE .co.za Domain Name',
      'No setup fee',
      '10GB disk space',
      'Unlimited traffic',
      '50 Email accounts',
      '5 MYSQL Databases',
      'Latest PHP version',
      'Free website builder',
      '400+ one-click install scripts',
      'Free SSL certificate',
      SUPPORT_FEATURE,
    ],
  },
  {
    name: 'Basic',
    slug: 'basic-monthly',
    priceZar: 129,
    tagline: 'Everything you need to launch',
    features: [
      'cPanel Control Panel Access',
      'FREE .co.za Domain Name',
      'No setup fee',
      '25GB disk space',
      'Unlimited traffic',
      '50 Email accounts',
      '10 MYSQL Databases',
      'Latest PHP version',
      'Free website builder',
      '400+ one-click install scripts',
      'Free SSL certificate',
      SUPPORT_FEATURE,
    ],
  },
  {
    name: 'Super',
    slug: 'super-monthly',
    priceZar: 289,
    popular: true,
    features: [
      'cPanel Control Panel Access',
      'FREE .co.za Domain Name',
      'No setup fee',
      '30GB disk space',
      'Unlimited traffic',
      '100 Email accounts',
      '100 MYSQL Databases',
      'Latest PHP version',
      'Free website builder',
      '400+ one-click install scripts',
      'Free SSL certificate',
      SUPPORT_FEATURE,
    ],
  },
  {
    name: 'Ultimate',
    slug: 'ultimate-monthly',
    priceZar: 499,
    features: [
      'cPanel Control Panel Access',
      'FREE .co.za Domain Name',
      'No setup fee',
      '50GB disk space',
      'Unlimited traffic',
      'Unlimited Email accounts',
      'Unlimited MYSQL Databases',
      'Latest PHP version',
      'Free Premium website builder',
      '400+ one-click install scripts',
      'Free SSL certificate',
      SUPPORT_FEATURE,
    ],
  },
];

/** Convenience: plans keyed by slug for O(1) lookup. */
export const hostingPlansBySlug: Record<string, HostingPlan> = Object.fromEntries(
  hostingPlans.map((plan) => [plan.slug, plan]),
);
